import { memo } from 'react'

import { CopyAll, Warning } from '@mui/icons-material'
import { Box, IconButton, Paper, styled, Tooltip, Typography } from '@mui/material'
// NOTE: exp-analytics not deployed — restore these into their imports with the analytics panel below.
// import { ButtonBase } from '@mui/material'
// import { motion } from 'motion/react'
import { useQuery } from '@tanstack/react-query'
import CopyToClipboard from 'react-copy-to-clipboard'
import { useTranslation } from 'react-i18next'

import { ErrorBoundary } from '@shared/components/ErrorHandling/ErrorBoundary'
import { CodeInline } from '@shared/components/MaterialUIEnhancements/CodeInline'
import { LoadingIndicator, NotFound } from '@shared/layouts'
import GrafanaLogo from 'assets/logos/grafana.svg?react'
import { EventAlerts, EventState } from 'domains/Workspaces/Events/Event/Dashboard'
import { EventProgress } from 'domains/Workspaces/Events/Event/Dashboard/EventProgress'
import { eventDashboardRoute } from 'pages/NWPlatform/Workspaces/Workspace/Events/Event'
import { eventQueryKeys } from 'services/queryKeys'
// NOTE: exp-analytics is currently not deployed; the analytics panel is replaced by a placeholder below.
// import { reportQueryKeys } from 'services/queryKeys/exp-analytics'
import { RefetchIntervals } from 'types/query'
import { getHealthColor, getHealthIcon } from 'utils'

const StyledGrafanaLogo = styled(GrafanaLogo)({
  width: 60,
})

const DashboardRaw = () => {
  const { t } = useTranslation(['common', 'domain'])

  const { eventId } = eventDashboardRoute.useParams()

  const eventQuery = useQuery({ ...eventQueryKeys.detail({ eventId }), refetchInterval: RefetchIntervals.NORMAL })
  const { data: event } = eventQuery

  // NOTE: exp-analytics is currently not deployed; the analytics dashboards query is disabled.
  // const expManifestAnalyticsQuery = useQuery({
  //   ...reportQueryKeys.dashboards({ groupId: `manifest-${event?.expManifestId}` }),
  //   enabled: eventQuery.isSuccess,
  // })

  if (eventQuery.isLoading) {
    return <LoadingIndicator />
  }

  if (!event) {
    return <NotFound />
  }

  return (
    <Box sx={{ display: 'grid', gridAutoRows: 'auto', rowGap: 4 }}>
      <ErrorBoundary
        fallback={() => (
          <Paper
            sx={{
              height: 130,
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              columnGap: 2,
            }}
            elevation={0}
          >
            <Warning color="warning" />
            <Typography sx={{ color: 'warning.main' }} variant="heading-lg">
              {t('domain:Workspace.Event.componentUnavailable')}
            </Typography>
            <Warning color="warning" />
          </Paper>
        )}
      >
        <EventProgress event={event} />
      </ErrorBoundary>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'stretch',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        {[
          {
            title: t('domain:Workspace.Event.healthState'),
            description: t('domain:Workspace.Event.noIssues'),
            icon: getHealthIcon(event.health, {
              fontSize: 'medium',
              sx: { color: (theme) => getHealthColor(event.health, theme.palette.mode), cursor: 'pointer' },
            }),
          },
        ].map((item) => (
          <Box
            sx={{ flex: 1, py: 3, px: 4, minWidth: 320, bgcolor: 'background.levels.100', borderRadius: 1 }}
            key={item.title}
          >
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="heading-md">{item.title}</Typography>
              {item.icon}
            </Box>
            <Typography variant="text">{item.description}</Typography>
          </Box>
        ))}
        <EventState eventStatus={event.status} />
        <EventAlerts eventAlerts={event.alerts} />
      </Box>

      <Paper sx={{ px: 4, py: 3, minWidth: 320, maxWidth: 480, width: '100%' }}>
        <Typography sx={{ fonWeight: 500, mb: 1 }} variant="heading-md">
          Details
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', rowGap: 1 }}>
          <Box>
            <Typography variant="heading-xs">Event ID</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 1 }}>
              <CodeInline sx={{ width: '100%' }} noWrap>
                {event.id}
              </CodeInline>
              <CopyToClipboard text={event.id}>
                <Tooltip title={t('clickToCopy')} placement="right">
                  <IconButton size="small">
                    <CopyAll fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              </CopyToClipboard>
            </Box>
          </Box>
          <Box>
            <Typography variant="heading-xs">Task ID</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 1 }}>
              <CodeInline sx={{ width: '100%' }} noWrap>
                {event.taskId}
              </CodeInline>
              <CopyToClipboard text={event.taskId}>
                <Tooltip title={t('clickToCopy')} placement="right">
                  <IconButton size="small">
                    <CopyAll fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              </CopyToClipboard>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* exp-analytics not deployed — original Grafana analytics panel preserved below for easy restore.
          Re-enable: uncomment the reportQueryKeys import, the expManifestAnalyticsQuery, the
          motion/ButtonBase imports above, and this block; then delete the placeholder Paper underneath.

      {expManifestAnalyticsQuery.isSuccess && (
        <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 2 }}>
          {!!expManifestAnalyticsQuery.data.manifestTimeline?.length && (
            <ButtonBase
              sx={{ font: 'unset' }}
              component={motion.a}
              whileHover={{
                scale: 1.05,
              }}
              href={expManifestAnalyticsQuery.data.manifestTimeline}
            >
              <Paper
                sx={{
                  p: 3,
                  width: 160,
                  display: 'flex',
                  alignItems: 'center',
                  flexDirection: 'column',
                  textDecoration: 'none',
                }}
              >
                <StyledGrafanaLogo sx={{ mb: 2 }} />
                <Typography variant="heading-sm">Manifest</Typography>
              </Paper>
            </ButtonBase>
          )}

          {!!expManifestAnalyticsQuery.data.wallclockTimeline?.length && (
            <ButtonBase
              sx={{ font: 'unset' }}
              component={motion.a}
              whileHover={{
                scale: 1.05,
              }}
              href={expManifestAnalyticsQuery.data.wallclockTimeline}
            >
              <Paper
                sx={{
                  p: 3,
                  width: 160,
                  display: 'flex',
                  alignItems: 'center',
                  flexDirection: 'column',
                  textDecoration: 'none',
                }}
              >
                <StyledGrafanaLogo sx={{ mb: 2 }} />
                <Typography variant="heading-sm">Wallclock</Typography>
              </Paper>
            </ButtonBase>
          )}
        </Box>
      )}
      */}
      <Paper
        sx={{
          px: 4,
          py: 3,
          minWidth: 320,
          maxWidth: 480,
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          columnGap: 2,
        }}
        elevation={0}
      >
        <StyledGrafanaLogo />
        <Box>
          <Typography variant="heading-sm">{t('domain:Workspace.Event.analytics')}</Typography>
          <Typography sx={{ color: 'text.secondary' }} variant="text-sm">
            {t('domain:Workspace.Event.analyticsUnavailable')}
          </Typography>
        </Box>
      </Paper>
    </Box>
  )
}

export const Dashboard = memo(DashboardRaw)
